import type { FileUpload } from 'graphql-upload/processRequest.mjs';
import Address, { CreateAddressInput } from './address.js';
import { Answer, ApplicationAnswers } from './answers/index.js';
import Case from './case.js';
import { LinkAttempt } from './caseParticipant.js';
import Document from './document.js';
import { DateRange } from './filter.js';
import ProgramReferral from './programReferral.js';
import User from './user.js';

export interface Application {
  id: string;
  displayId: string;
  caseId: string;
  submitterId: string;
  requestedAmount: number;
  answers: Partial<ApplicationAnswers>;
  applicationAnswers?: Answer[];
  createdAt: Date;
  updatedAt: Date;
  deactivatedAt?: Date;
  submittedAt?: Date;
  submitter: User;
  case?: Case;
  documents?: Document[];
  addresses?: Address[];
  versions?: ApplicationVersion[];
  latestVersion?: ApplicationVersion;
  score?: ApplicationScore;
  referralId?: string;
  referral?: ProgramReferral;
  verification?: ApplicationVerification;
  linkAttempts?: LinkAttempt[];
}
export default Application;

export interface ApplicationFilter {
  id: string;
  programId: string;
  search: string;
  submitterId: string;
}

export interface CreateApplicationInput {
  programId: string;
  userId: string;
}

export interface AggregateApplicationFilter {
  programId?: string;
  createdAt?: DateRange;
  submittedAt?: DateRange;
}

export interface AggregateApplication {
  count: number;
}

export enum ApplicationPaymentStatus {
  Unclaimed = 'Unclaimed',
  PaymentProcessing = 'Payment Processing',
  Failed = 'Failed',
  Paid = 'Paid',
  NA = 'N/A',
}

export interface ApplicationVerification {
  id: string;
  applicationVersionId: string;
  confidence: number;
  createdAt: Date;
  deactivatedAt?: Date;
  // TODO use generated types
  service: string;
  details: {
    key: string;
    description: string;
    confidence: number;
    weight: number;
    diff: { provided: string; verified: string } | null;
  }[];
  metadata: { key: string; value: string }[];
}

export interface ApplicationVersion {
  id: string;
  application: Application;
  applicationId: string;
  creatorId: string;
  createdAt: Date;
  updatedAt: Date;
  deactivatedAt?: Date;
  description?: string;
  // Output of the rules-engine persisted on the version row
  // Accept any partner-defined wording. System values like
  // 'NotEvaluated' and 'FailedToEvaluate' may still appear.
  eligibility?: string;
  // Optional human-readable explanation for the eligibility value
  eligibilityReason?: string;
  // Timestamp of when this version was last evaluated by the rules engine
  lastEvaluatedAt?: Date | null;
  // Numeric snapshot for convenience; historical scores remain in ApplicationScore
  versionScore?: number | null;
  // Audit: which ruleset and version produced this decision
  rulesetId?: string;
  rulesetVersion?: number | null;
  answers: Partial<ApplicationAnswers>;
  applicationAnswers?: Answer[];
  creator: User;
  verification?: ApplicationVerification;
}

export interface NewAddressUpdate {
  key: string;
  address: CreateAddressInput;
}

export interface CopyAddressUpdate {
  key: string;
  copyKey: string;
}

export interface ExistingAddressUpdate {
  key: string;
  addressId: string;
}

export type ApplicationAddressUpdate = NewAddressUpdate | CopyAddressUpdate | ExistingAddressUpdate;

export function isNewAddress(address: ApplicationAddressUpdate): address is NewAddressUpdate {
  return 'address' in address;
}

export function isExistingAddress(
  address: ApplicationAddressUpdate,
): address is ExistingAddressUpdate {
  return 'addressId' in address;
}

export interface NewDocumentUpdate {
  key: string;
  files: Promise<FileUpload>[];
}

export interface ExistingDocumentUpdate {
  key: string;
  documentIds: string[];
}

export type ApplicationDocumentUpdate = NewDocumentUpdate | ExistingDocumentUpdate;

export function isNewDocument(document: ApplicationDocumentUpdate): document is NewDocumentUpdate {
  return 'files' in document;
}

export interface ApplicationContentInput {
  answers: Partial<ApplicationAnswers>;
  addresses: ApplicationAddressUpdate[];
  documents: ApplicationDocumentUpdate[];
}

export interface AddApplicationVersionInput {
  id: string;
  content?: ApplicationContentInput;
  description?: string;
  versionId?: string;
}

export interface UpdateApplicationInput {
  id: string;
  content: ApplicationContentInput;
}

export interface ApplicationScore {
  id: string;
  applicationVersionId: string;
  score: number;
  algorithmVersion: number;
  createdAt: Date;
  updatedAt: Date;
  deactivatedAt?: Date;
}

export enum ApplicationQuestionKeys {
  MailingAddress = 'address.mailingAddressId',
}

export enum VerificationStatus {
  Unverified = 'Unverified',
  PartiallyVerified = 'PartiallyVerified',
  Verified = 'Verified',
}

export enum ApplicationScoreTier {
  ONE = 1,
  TWO = 2,
  THREE = 3,
}

export interface ApplicationSubmission {
  id: string;
  currentVersion: ApplicationVersion;
  latestSubmission: ApplicationVersion;
  submitter: User;
  createdAt: Date;
  updatedAt: Date;
  displayId: string;
  versions: ApplicationVersion[];
  documents: Document[];
  addresses: Address[];
}
